export { SEO } from "./SEO";

import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import DOMPurify from "isomorphic-dompurify";
import { BadgeCheck, Shield, Video, X, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const JeekotaLogo = ({ size = 40, className = "" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_2515fe7f-9b3f-475c-8b2b-feb23747bb05/artifacts/qq6gufzs_WhatsApp%20Image%202026-04-07%20at%206.27.37%20PM.jpeg"
    alt="Jeekota Logo"
    width={size}
    height={size}
    className={`rounded-xl ${className}`}
    style={{ objectFit: 'cover' }}
  />
);

export const VerificationTick = ({ type, size = 16 }) => {
  if (!type) return null;
  const colors = { grey: "#6b7280", green: "#22c55e", orange: "#f97316", blue: "#3b82f6", purple: "#a855f7" };
  return (
    <BadgeCheck 
      size={size} 
      className="inline-block ml-1 shrink-0 align-middle" 
      fill={colors[type]} 
      stroke="white"
    />
  );
};

export const TrustScore = ({ score }) => (
  <div className="flex items-center gap-1">
    <Shield size={14} className="text-jeekota-primary" />
    <span className="text-sm font-medium">{score}/100</span>
  </div>
);

const RENDER_SANITIZE_CONFIG = {
  ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'a', 'div', 'ol', 'ul', 'li'],
  ALLOWED_ATTR: ['href', 'target', 'rel'],
};

const sanitizeForRender = (html) => {
  let clean = DOMPurify.sanitize(html, RENDER_SANITIZE_CONFIG);
  // Strip invisible Unicode characters
  clean = clean
    .replace(/[\u200B\u200C\u200D\u200E\u200F\uFEFF\u00AD]/g, '')
    .replace(/[\u2028\u2029]/g, ' ')
    .replace(/\u00A0/g, ' ');
  // Force links to open in new tab
  clean = clean.replace(/<a\s(?![^>]*target=)/g, '<a target="_blank" rel="noopener noreferrer" ');
  return clean;
};

export const RenderWithMentions = ({ text, className = "" }) => {
  if (!text) return null;
  const parts = text.split(/(@\w+)/g);
  return (
    <span className={className}>
      {parts.map((part, i) => {
        if (part.startsWith('@') && part.length > 1) {
          const username = part.slice(1);
          return (
            <Link
              key={i}
              to={`/user/${username}`}
              className="text-jeekota-primary hover:underline font-medium"
              onClick={(e) => { e.stopPropagation(); }}
            >
              {part}
            </Link>
          );
        }
        return <span key={i} dangerouslySetInnerHTML={{ __html: sanitizeForRender(part) }} />;
      })}
    </span>
  );
};

export const MediaUpload = ({ onFilesChange, maxImages = 6, maxVideos = 1 }) => {
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const currentImgs = files.filter(f => f.type.startsWith('image/')).length;
    const currentVids = files.filter(f => f.type.startsWith('video/')).length;

    const newFiles = [];
    for (const file of selectedFiles) {
      if (file.type.startsWith('image/') && currentImgs + newFiles.filter(f => f.type.startsWith('image/')).length >= maxImages) {
        toast.error(`Maximum ${maxImages} images allowed`);
        continue;
      }
      if (file.type.startsWith('video/')) {
        if (currentVids + newFiles.filter(f => f.type.startsWith('video/')).length >= maxVideos) {
          toast.error(`Maximum ${maxVideos} video allowed`);
          continue;
        }
        if (file.size > 20 * 1024 * 1024) {
          toast.error("Video must be under 20MB");
          continue;
        }
      }
      newFiles.push({
        id: `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name: file.name, type: file.type, size: file.size,
        file: file, preview: URL.createObjectURL(file)
      });
    }
    const updatedFiles = [...files, ...newFiles];
    setFiles(updatedFiles);
    onFilesChange?.(updatedFiles);
    e.target.value = '';
  };

  const removeFile = (fileId) => {
    const updatedFiles = files.filter(f => f.id !== fileId);
    setFiles(updatedFiles);
    onFilesChange?.(updatedFiles);
  };

  const currentImages = files.filter(f => f.type.startsWith('image/')).length;
  const currentVideos = files.filter(f => f.type.startsWith('video/')).length;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Button type="button" variant="outline" size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={currentImages >= maxImages && currentVideos >= maxVideos}
          data-testid="media-upload-btn"
        >
          <Upload size={16} className="mr-2" /> Add Media
        </Button>
        <span className="text-xs text-jeekota-text-secondary">
          {currentImages}/{maxImages} images, {currentVideos}/{maxVideos} video
        </span>
        <input ref={fileInputRef} type="file" accept="image/*,video/*" multiple
          onChange={handleFileSelect} className="hidden" />
      </div>
      {files.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {files.map((file) => (
            <div key={file.id} className="relative group">
              {file.type.startsWith('image/') ? (
                <img src={file.preview} alt={file.name} className="w-full h-24 object-cover rounded-lg" />
              ) : (
                <div className="w-full h-24 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Video size={24} className="text-gray-400" />
                </div>
              )}
              <button type="button" onClick={() => removeFile(file.id)}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
